/**
 * SectionRenderer — render de una sección del artifact según su `type`.
 *
 * Recibe la metadata estructural de la sección (`type`, `required`),
 * el título localizado y el `value` del content (cuyo shape depende
 * del type). Cada caso es defensivo: si el value no matchea el shape
 * esperado, render un placeholder en vez de explotar.
 *
 * Block types implementados:
 *   - Texto: rich_text · plain_text · key_value_grid
 *   - Listas: bullet_list · numbered_list
 *   - Tabla: table (genérica) — structured_table tipada en Commit E
 *   - Diagramas: diagram (Mermaid genérico) — sub-tipos en Commit E
 *   - Otros: code_block · structured (debug)
 *   - Contenido fijo: fixed_heading (contenedor recursivo en Commit C) ·
 *     fixed_block (callout)
 *
 * Tipos NO implementados aún (image_upload, file_upload, embed_canon →
 * B.7+) caen al render `unsupported` con mensaje claro. NO inventamos UI
 * fake. Patrones compuestos como ADR (Context/Decision/Consequences) o
 * Acceptance Criteria (Given/When/Then) NO son tipos atómicos — se
 * construyen con primitivos anidados en un fixed_heading.
 */

import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BookOpen,
  CheckCircle2,
  CheckSquare,
  FileQuestion,
  Info,
  Lightbulb,
  ListChecks,
  ShieldAlert,
  Square,
  StickyNote,
  TriangleAlert,
} from 'lucide-react'

import { toIntlLocale } from '../../../i18n/config'
import { normalizeTableSchema } from '../../../lib/structuredTableAdapter'
import { RichTextEditor } from '../../editor/RichTextEditor'
import { isDocEmpty, isTipTapDoc } from '../../editor/value'
import {
  isMermaidType,
  isSupportedBlockType,
  type TemplateSectionMeta,
} from './section_types'

interface SectionRendererProps {
  meta: TemplateSectionMeta
  title: string
  /** Valor del content para esta section_id. undefined si la section
   * existe en el template pero no en el content (artifact creado pero
   * sección sin llenar). */
  value: unknown
  /** Mapa column_key → label (del locale activo) — usado por
   * `structured_table` para mostrar headers localizados. Opcional.
   * Para shape v2 con `table_config`, los labels se derivan automáticamente
   * del shape inline y este prop puede omitirse. */
  columnLabels?: Record<string, string>
  /** Locale del proyecto/artefacto para resolver labels v2 cuando
   * `columnLabels` no se pasa explícito. Default `'es'`. */
  locale?: string
  /** Muestra el `section_id` junto al título. Es un identificador técnico
   *  del template: quien no lee los recursos internos del tenant no lo ve
   *  (OT-0017). El atributo `data-section-id` se conserva siempre. */
  showSectionId?: boolean
}

export function SectionRenderer({
  meta,
  title,
  value,
  columnLabels,
  locale = 'es',
  showSectionId = true,
}: SectionRendererProps) {
  const { t } = useTranslation('projects')

  // Caso 1: sin contenido capturado — placeholder honesto.
  // Para rich_text aceptamos también docs TipTap "vacíos" (doc con
  // solo párrafos sin texto) — sin esto, el viewer renderizaría un
  // bloque con margen pero sin contenido.
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0) ||
    (meta.type === 'rich_text' && isTipTapDoc(value) && isDocEmpty(value))

  return (
    // `data-section-id`: usado por `useTextSelection` (Doc 21 Fase 5.e+)
    // para detectar a qué sección pertenece el texto seleccionado y
    // auto-poblar el combo del comment form.
    <article
      className="bg-surface border border-line rounded-md p-5"
      data-section-id={meta.section_id}
    >
      <header className="mb-3 flex items-baseline gap-3 flex-wrap">
        <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
        {meta.required ? (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-muted text-ink-subtle">
            {t('official_viewer.section_required_badge')}
          </span>
        ) : null}
        {showSectionId ? (
          <span className="ml-auto font-mono text-[10px] text-ink-subtle">
            {meta.section_id}
          </span>
        ) : null}
      </header>

      {meta.type === 'fixed_heading' ? (
        // fixed_heading no tiene "value" del usuario — solo es decorativo
        // (un divider visual). Render del title es el que ya hizo el
        // header arriba; aquí mostramos un hint sutil.
        <FixedHeadingValue />
      ) : isEmpty ? (
        <EmptyValue />
      ) : !isSupportedBlockType(meta.type) ? (
        <UnsupportedType type={String(meta.type)} />
      ) : meta.type === 'rich_text' ? (
        <RichTextValue value={value} />
      ) : meta.type === 'plain_text' ? (
        <PlainTextValue value={value} />
      ) : meta.type === 'bullet_list' ? (
        <ListValue value={value} ordered={false} />
      ) : meta.type === 'numbered_list' ? (
        <ListValue value={value} ordered={true} />
      ) : meta.type === 'checklist' ? (
        <ChecklistValue value={value} />
      ) : meta.type === 'key_value_grid' ? (
        <KeyValueGridValue value={value} />
      ) : meta.type === 'structured' ? (
        <StructuredValue value={value} />
      ) : meta.type === 'code_block' ? (
        <CodeBlockValue value={value} />
      ) : meta.type === 'structured_table' ? (
        <StructuredTableValue
          value={value}
          meta={meta}
          {...(columnLabels ? { columnLabels } : {})}
          locale={locale}
        />
      ) : isMermaidType(meta.type) ? (
        <DiagramValue value={value} />
      ) : meta.type === 'fixed_block' ? (
        <FixedBlockValue
          value={value}
          {...(typeof meta['variant'] === 'string'
            ? { variant: meta['variant'] }
            : {})}
        />
      ) : null}
    </article>
  )
}

// ─── Empty + unsupported ────────────────────────────────────────────────────
function EmptyValue() {
  const { t } = useTranslation('projects')
  return (
    <p className="font-body text-sm text-ink-subtle italic flex items-center gap-2">
      <FileQuestion className="w-4 h-4" />
      {t('official_viewer.missing_section_content')}
    </p>
  )
}

function UnsupportedType({ type }: { type: string }) {
  const { t } = useTranslation('projects')
  return (
    <div className="rounded-md border border-review/40 bg-review-soft/40 p-3 flex items-start gap-2">
      <ShieldAlert className="w-4 h-4 text-review mt-0.5 shrink-0" />
      <p className="font-body text-xs text-ink-muted">
        {t('official_viewer.unsupported_block_type', { type })}
      </p>
    </div>
  )
}

// ─── Rich text ──────────────────────────────────────────────────────────────
function RichTextValue({ value }: { value: unknown }) {
  // B.6.b: el editor escribe TipTap JSON; los proyectos legacy pueden
  // aún tener strings (textarea simple). `RichTextEditor` en mode='view'
  // normaliza ambos vía `coerceToTipTapDoc` y los renderiza con la
  // misma config visual que el editor — paridad garantizada.
  return <RichTextEditor value={value as never} mode="view" />
}

// ─── Plain text ─────────────────────────────────────────────────────────────
function PlainTextValue({ value }: { value: unknown }) {
  if (typeof value !== 'string') return <FallbackJson value={value} />
  return (
    <p className="font-body text-sm text-ink whitespace-pre-line leading-relaxed">
      {value}
    </p>
  )
}

// ─── Bullet / numbered list ────────────────────────────────────────────────
function ListValue({
  value,
  ordered,
}: {
  value: unknown
  ordered: boolean
}) {
  if (!Array.isArray(value)) return <FallbackJson value={value} />

  // Items pueden ser strings o {text: string} o {value: string}
  const items: string[] = value
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>
        if (typeof obj['text'] === 'string') return obj['text']
        if (typeof obj['value'] === 'string') return obj['value']
      }
      return null
    })
    .filter((s): s is string => s !== null)

  if (items.length === 0) {
    return <FallbackJson value={value} />
  }

  const ListTag = ordered ? 'ol' : 'ul'
  const listClass = ordered
    ? 'list-decimal ml-5 space-y-1.5 font-body text-sm text-ink'
    : 'list-disc ml-5 space-y-1.5 font-body text-sm text-ink'

  return (
    <ListTag className={listClass}>
      {items.map((it, i) => (
        <li key={i} className="leading-relaxed">
          {it}
        </li>
      ))}
    </ListTag>
  )
}

// ─── Checklist (items con estado completado / no) ──────────────────────────
interface ChecklistItem {
  text: string
  done: boolean
}

/** Parse defensivo de un item: acepta `{text, done}` o el legacy
 * string (sin done — default false). */
function parseChecklistItem(item: unknown): ChecklistItem | null {
  if (typeof item === 'string') {
    const s = item.trim()
    return s ? { text: s, done: false } : null
  }
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>
    const text = typeof obj['text'] === 'string' ? obj['text'].trim() : ''
    const done = obj['done'] === true
    return text ? { text, done } : null
  }
  return null
}

function ChecklistValue({ value }: { value: unknown }) {
  if (!Array.isArray(value)) return <FallbackJson value={value} />
  const items: ChecklistItem[] = value
    .map(parseChecklistItem)
    .filter((it): it is ChecklistItem => it !== null)
  if (items.length === 0) return <FallbackJson value={value} />
  return (
    <ul className="space-y-1.5 font-body text-sm">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          {item.done ? (
            <CheckSquare className="w-4 h-4 mt-0.5 shrink-0 text-official" />
          ) : (
            <Square className="w-4 h-4 mt-0.5 shrink-0 text-ink-subtle" />
          )}
          <span
            className={
              item.done
                ? 'line-through text-ink-muted leading-relaxed'
                : 'text-ink leading-relaxed'
            }
          >
            {item.text}
          </span>
        </li>
      ))}
    </ul>
  )
}

// ─── Key value grid ────────────────────────────────────────────────────────
function KeyValueGridValue({ value }: { value: unknown }) {
  if (!Array.isArray(value)) return <FallbackJson value={value} />

  const pairs: Array<{ key: string; value: string }> = value
    .map((item) => {
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>
        const k =
          typeof obj['key'] === 'string'
            ? obj['key']
            : typeof obj['name'] === 'string'
              ? obj['name']
              : null
        const v =
          typeof obj['value'] === 'string'
            ? obj['value']
            : obj['value'] !== undefined
              ? String(obj['value'])
              : null
        if (k && v !== null) return { key: k, value: v }
      }
      return null
    })
    .filter((p): p is { key: string; value: string } => p !== null)

  if (pairs.length === 0) return <FallbackJson value={value} />

  return (
    <dl className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
      {pairs.map((pair, i) => (
        <div key={i} className="contents">
          <dt className="font-medium text-ink-muted">{pair.key}</dt>
          <dd className="text-ink">{pair.value}</dd>
        </div>
      ))}
    </dl>
  )
}

// ─── Structured (JSON viewer pragmatic) ────────────────────────────────────
function StructuredValue({ value }: { value: unknown }) {
  return <FallbackJson value={value} />
}

// ─── Fixed block (callout con texto fijo del template) ────────────────────
/** Mapa variant → estilo visual + icono + clase del icono.
 * El template author elige el variant en el Designer; el renderer
 * consume `meta['variant']`. Default: `note`. */
const FIXED_BLOCK_VARIANT_STYLE: Record<
  string,
  {
    container: string
    icon: typeof StickyNote
    iconClass: string
  }
> = {
  note: {
    container: 'border-line bg-surface-muted/40',
    icon: StickyNote,
    iconClass: 'text-ink-muted',
  },
  info: {
    container: 'border-brand/30 bg-brand-soft/30',
    icon: Info,
    iconClass: 'text-brand',
  },
  tip: {
    container: 'border-official/30 bg-official-soft/30',
    icon: Lightbulb,
    iconClass: 'text-official',
  },
  warning: {
    container: 'border-review/40 bg-review-soft/40',
    icon: TriangleAlert,
    iconClass: 'text-review',
  },
  critical: {
    container: 'border-critical/40 bg-critical-soft/40',
    icon: ShieldAlert,
    iconClass: 'text-critical',
  },
  success: {
    container: 'border-official/40 bg-official-soft/50',
    icon: CheckCircle2,
    iconClass: 'text-official',
  },
  reference: {
    container: 'border-line bg-canvas',
    icon: BookOpen,
    iconClass: 'text-ink',
  },
  instruction: {
    container: 'border-brand/30 bg-brand-soft/20',
    icon: ListChecks,
    iconClass: 'text-brand',
  },
}

/** Renderea el `body` localizado de un fixed_block. El value en runtime
 * siempre es `undefined` (los fixed_block no se llenan); el texto está
 * en `i18n.{lang}.sections[].body` y lo inyectamos por aparte vía el
 * orquestador. Por ahora aceptamos un `value` que sea `{ body: string }`
 * para visualizarlo en el preview del Designer. */
function FixedBlockValue({
  value,
  variant,
}: {
  value: unknown
  variant?: string
}) {
  let body = ''
  if (typeof value === 'string') {
    body = value
  } else if (value && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>
    if (typeof obj['body'] === 'string') body = obj['body']
  }
  if (!body.trim()) return <FallbackJson value={value} />
  const style =
    FIXED_BLOCK_VARIANT_STYLE[variant ?? 'note'] ??
    FIXED_BLOCK_VARIANT_STYLE['note']!
  const IconComp = style.icon
  return (
    <div
      className={`rounded-md border p-3 flex items-start gap-3 ${style.container}`}
    >
      <IconComp className={`w-4 h-4 mt-0.5 shrink-0 ${style.iconClass}`} />
      <p className="font-body text-sm text-ink whitespace-pre-line leading-relaxed">
        {body}
      </p>
    </div>
  )
}

// ─── Code block ───────────────────────────────────────────────────────────
function CodeBlockValue({ value }: { value: unknown }) {
  let code = ''
  let language = ''
  if (typeof value === 'string') {
    code = value
  } else if (value && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>
    if (typeof obj['code'] === 'string') code = obj['code']
    if (typeof obj['language'] === 'string') language = obj['language']
  }
  if (!code) return <FallbackJson value={value} />
  return (
    <div className="rounded-md border border-line overflow-hidden">
      {language ? (
        <div className="bg-surface-muted px-3 py-1.5 border-b border-line text-[10px] font-mono text-ink-subtle uppercase tracking-wider">
          {language}
        </div>
      ) : null}
      <pre className="bg-surface-muted/60 p-3 font-mono text-xs text-ink overflow-x-auto whitespace-pre">
        {code}
      </pre>
    </div>
  )
}

// ─── Fixed heading ─────────────────────────────────────────────────────────
function FixedHeadingValue() {
  const { t } = useTranslation('templates')
  return (
    <p className="font-body text-xs text-ink-subtle italic">
      {t('blocks.fixed_heading.hint')}
    </p>
  )
}

// ─── Structured table (typed columns) ─────────────────────────────────────
/**
 * Formatea una celda según el type de su columna.
 *
 * **Localización (Doc 19):** `locale` es el idioma del CONTENIDO del
 * proyecto (`project.content_language`), NO el idioma de la UI del
 * usuario. Un documento oficial en español se ve "01/06/2026" aunque
 * el usuario tenga la UI en inglés — el documento es es-MX por contrato.
 *
 * Reglas por tipo:
 *  - `date`: ISO `YYYY-MM-DD` → `Intl.DateTimeFormat(locale)` con
 *    formato corto (es-MX: `01/06/2026`, en: `06/01/2026`). Si el
 *    valor no parsea, devolvemos el raw como fallback honesto.
 *  - `number`: `Intl.NumberFormat(locale)` "smart default" — respeta
 *    los decimales que el valor traiga, agrega separadores de miles.
 *    `550000` → `550,000` (es-MX/en) / `550.000` (es-ES). `20` → `20`,
 *    `3.5` → `3.5`.
 *  - `currency` (Doc 19 §10): celda `{amount, code}` →
 *    `Intl.NumberFormat(locale, {style:'currency', currency: code})`.
 *    Si la celda omite `code`, usa `defaultCurrency` (column-level).
 *    Resultado: `$550,000.00 MXN`, `US$50.00`, `€8,500.00`.
 *  - `autonum`: prefijo + número con padding (no localizado, es ID).
 *  - cualquier otro: string crudo.
 */
export function formatStructuredCell(
  raw: unknown,
  type: string,
  rowIndex: number,
  prefix: string,
  pad: number,
  locale: string = 'es',
  defaultCurrency: string | null = null,
): string {
  if (type === 'autonum') {
    const n = rowIndex + 1
    const padded = pad > 0 ? String(n).padStart(pad, '0') : String(n)
    return `${prefix}${padded}`
  }
  if (raw === null || raw === undefined) return ''
  // Doc 19: `locale` puede llegar como '`es`' / '`en`' (sin región) desde
  // `project.content_language`. Lo elevamos a `'es-MX'` / '`en-US`' via
  // `toIntlLocale` antes de pasarlo a `Intl.*` — pasar `'es'` directo
  // hace que JS lo resuelva como `'es-ES'` (España: punto miles, coma
  // decimal), formato que confunde en MX/LatAm/US.
  const intlLocale = toIntlLocale(locale)
  if (type === 'date') {
    if (typeof raw === 'string' && raw.trim()) {
      // Parsear ISO `YYYY-MM-DD` como fecha LOCAL (no UTC). `new Date('2026-06-01')`
      // se interpreta como UTC y puede mostrar 31/05 en zonas negativas; armamos
      // los componentes explícitamente para evitar ese off-by-one.
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim())
      if (match) {
        const [, y, m, d] = match
        const dt = new Date(Number(y), Number(m) - 1, Number(d))
        try {
          return new Intl.DateTimeFormat(intlLocale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(dt)
        } catch {
          return raw
        }
      }
      return raw
    }
    return ''
  }
  if (type === 'number') {
    const num =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw.trim() && !Number.isNaN(Number(raw))
          ? Number(raw)
          : null
    if (num !== null && Number.isFinite(num)) {
      try {
        return new Intl.NumberFormat(intlLocale).format(num)
      } catch {
        return String(num)
      }
    }
    if (typeof raw === 'string' && raw.trim()) return raw
    return ''
  }
  if (type === 'currency') {
    // Doc 19 §10 — celda canónica `{amount: number, code?: string}`.
    // `code` por celda permite filas mixtas (ej. honorarios MXN +
    // hosting USD). Si la celda omite `code`, usamos el
    // `defaultCurrency` de la columna. Fallback honesto si ningún
    // código está disponible: render como number sin símbolo.
    let amount: number | null = null
    let code: string | null = null
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>
      const rawAmount = obj['amount']
      if (typeof rawAmount === 'number' && Number.isFinite(rawAmount)) {
        amount = rawAmount
      } else if (
        typeof rawAmount === 'string' &&
        rawAmount.trim() &&
        !Number.isNaN(Number(rawAmount))
      ) {
        amount = Number(rawAmount)
      }
      const rawCode = obj['code']
      if (typeof rawCode === 'string' && /^[A-Z]{3}$/.test(rawCode.trim())) {
        code = rawCode.trim()
      }
    } else if (typeof raw === 'number' && Number.isFinite(raw)) {
      // Tolerancia legacy: si la celda viene como number escalar (data
      // pre-migración v2.1.0), tratamos como amount + defaultCurrency.
      amount = raw
    }
    if (amount === null) return ''
    const effectiveCode = code ?? defaultCurrency ?? null
    if (!effectiveCode) {
      // Sin código alguno disponible: render como number plain.
      try {
        return new Intl.NumberFormat(intlLocale).format(amount)
      } catch {
        return String(amount)
      }
    }
    try {
      return new Intl.NumberFormat(intlLocale, {
        style: 'currency',
        currency: effectiveCode,
      }).format(amount)
    } catch {
      // ISO 4217 inválido → fallback a number + code raw.
      return `${new Intl.NumberFormat(intlLocale).format(amount)} ${effectiveCode}`
    }
  }
  if (typeof raw === 'string') return raw
  return String(raw)
}

/** Render del structured_table. Doc 24 §3 — soporta shape v1 (`columns`)
 * y v2 (`table_config.columns`) via `normalizeTableSchema`. Para v2,
 * los labels se derivan automáticamente del `i18n` inline de cada
 * columna; el caller puede sobreescribir con `columnLabels` explícito. */
function StructuredTableValue({
  value,
  meta,
  columnLabels,
  locale = 'es',
}: {
  value: unknown
  meta: TemplateSectionMeta
  columnLabels?: Record<string, string>
  locale?: string
}) {
  // Doc 24 §3 — adaptador v1↔v2: detecta `table_config` shape v2 y mapea
  // a `columns` lite. Si llega v2, `normalizeTableSchema` también deriva
  // los labels por locale activo y los usamos cuando el caller no pasó
  // `columnLabels` explícito (legacy v1).
  const normalized = normalizeTableSchema(
    meta as unknown as Parameters<typeof normalizeTableSchema>[0],
    locale,
  )
  const columns = normalized.columns
  const labels = columnLabels ?? normalized.columnLabels ?? {}
  if (columns.length === 0) {
    // Sin schema declarado — fallback honesto.
    return <FallbackJson value={value} />
  }
  let rows: Array<Record<string, unknown>> = []
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Array.isArray((value as Record<string, unknown>)['rows'])
  ) {
    rows = (value as Record<string, unknown>)[
      'rows'
    ] as Array<Record<string, unknown>>
  } else if (Array.isArray(value)) {
    // Tolerancia: si el value es directamente el array de rows.
    rows = value as Array<Record<string, unknown>>
  }
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm">
        <thead className="bg-surface-muted/60">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={
                  col.type === 'number' ||
                  col.type === 'autonum' ||
                  col.type === 'currency'
                    ? 'px-3 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-ink-subtle border-b border-line'
                    : 'px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-ink-subtle border-b border-line'
                }
              >
                {labels[col.key] ?? col.key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-3 text-center font-body text-xs text-ink-subtle italic"
              >
                — sin filas —
              </td>
            </tr>
          ) : (
            rows.map((row, rIdx) => (
              <tr
                key={rIdx}
                className="border-b border-line last:border-0 hover:bg-surface-muted/30"
              >
                {columns.map((col) => {
                  const raw = row[col.key]
                  const text = formatStructuredCell(
                    raw,
                    col.type,
                    rIdx,
                    col.prefix ?? '',
                    typeof col.pad === 'number' ? col.pad : 0,
                    locale,
                    col.defaultCurrency ?? null,
                  )
                  // Render distinto para enum/select: badge si tiene
                  // valor; para autonum: mono right-aligned.
                  if (col.type === 'autonum') {
                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 align-top font-mono text-xs text-ink-subtle text-right"
                      >
                        {text}
                      </td>
                    )
                  }
                  if (
                    (col.type === 'enum' || col.type === 'select') &&
                    text
                  ) {
                    // Doc 24 v2 — el value persistido es snake_case; el
                    // viewer muestra el label localizado del enum cuando
                    // existe (`optionLabels[value]`).
                    const display = col.optionLabels?.[text] ?? text
                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 align-top font-body text-sm text-ink"
                      >
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-brand-soft text-brand text-xs font-medium">
                          {display}
                        </span>
                      </td>
                    )
                  }
                  if (col.type === 'number' || col.type === 'currency') {
                    // Currency comparte el alineamiento de number:
                    // right-aligned, mono, tabular-nums para que los
                    // montos se lean en columna alineados por dígitos.
                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 align-top font-mono text-sm text-ink text-right tabular-nums"
                      >
                        {text}
                      </td>
                    )
                  }
                  if (col.type === 'date' && text) {
                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 align-top font-mono text-sm text-ink"
                      >
                        {text}
                      </td>
                    )
                  }
                  return (
                    <td
                      key={col.key}
                      className="px-3 py-2 align-top font-body text-sm text-ink whitespace-pre-line"
                    >
                      {text}
                    </td>
                  )
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

// ─── Diagram (Mermaid) ─────────────────────────────────────────────────────
/** Extrae el código Mermaid del value (string crudo o `{ source: string }`). */
export function parseDiagramSource(value: unknown): string {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>
    if (typeof obj['source'] === 'string') return obj['source']
  }
  return ''
}

function DiagramValue({ value }: { value: unknown }) {
  const source = parseDiagramSource(value).trim()
  if (!source) return <FallbackJson value={value} />
  return <MermaidRender source={source} />
}

/** Render mermaid con dynamic import — la lib pesa ~1MB y solo se carga
 * cuando aparece un bloque diagram en pantalla. Usa useId() para garantizar
 * un id único por instancia (mermaid lo requiere). */
export function MermaidRender({ source }: { source: string }) {
  const { t } = useTranslation('templates')
  const reactId = useId()
  const renderId = useRef(`mermaid-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`)
  // Resultado del último render, con el código que lo produjo: `pending`
  // es "hay código y el resultado no es de este código".
  const [rendered, setRendered] = useState<{
    source: string
    svg: string
    error: string | null
  } | null>(null)
  const hasSource = source.trim() !== ''
  const current = rendered?.source === source ? rendered : null
  const pending = hasSource && current === null
  const error = current?.error ?? null
  // Mientras re-renderiza se sigue mostrando el SVG anterior.
  const svg = hasSource ? (rendered?.svg ?? '') : ''

  useEffect(() => {
    if (!source.trim()) return
    let cancelled = false
    void (async () => {
      try {
        const mod = await import('mermaid')
        const mermaid = mod.default
        // mermaid 12 cambió los defaults de layout, look y ancho de etiqueta.
        // Estos valores conservan el aspecto con el que se aprobaron los
        // documentos existentes (los de mermaid 11). `layout` va por tipo de
        // diagrama: global también se aplicaría al mindmap y lo reacomodaría.
        mermaid.initialize({
          startOnLoad: false,
          theme: 'neutral',
          securityLevel: 'strict',
          fontFamily: 'inherit',
          look: 'classic',
          flowchart: { layout: 'dagre', wrappingWidth: 200 },
          state: { layout: 'dagre' },
          class: { layout: 'dagre' },
        })
        const { svg: output } = await mermaid.render(
          renderId.current,
          source,
        )
        if (!cancelled) setRendered({ source, svg: output, error: null })
      } catch (err) {
        if (!cancelled) {
          setRendered({
            source,
            svg: '',
            error: err instanceof Error ? err.message : String(err),
          })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [source])

  if (error) {
    return (
      <div className="rounded-md border border-critical/40 bg-critical-soft/40 p-3 space-y-2">
        <p className="font-mono text-[10px] uppercase tracking-wider font-semibold text-critical">
          {t('blocks.diagram.parse_error')}
        </p>
        <pre className="bg-canvas border border-line rounded p-2 font-mono text-xs whitespace-pre overflow-x-auto text-ink-muted">
          {source}
        </pre>
        <p className="font-mono text-[11px] text-critical break-words">
          {error}
        </p>
      </div>
    )
  }
  if (pending && !svg) {
    return (
      <p className="font-body text-xs text-ink-subtle italic">
        {t('blocks.diagram.rendering')}
      </p>
    )
  }
  return (
    <div
      className="overflow-x-auto bg-canvas border border-line rounded-md p-3 [&_svg]:max-w-full [&_svg]:h-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}

// ─── Fallback JSON ──────────────────────────────────────────────────────────
function FallbackJson({ value }: { value: unknown }) {
  return (
    <pre className="bg-surface-muted border border-line rounded-md p-3 font-mono text-xs text-ink-muted overflow-x-auto">
      {safeJsonStringify(value)}
    </pre>
  )
}

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}
